import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Modal, OverlayTrigger, ProgressBar, Row, Table, Tooltip as BootstrapTooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FaAward,
  FaBoxes,
  FaBuilding,
  FaCar,
  FaChartLine,
  FaCheckCircle,
  FaClipboardCheck,
  FaClipboardList,
  FaCogs,
  FaDatabase,
  FaDownload,
  FaEnvelope,
  FaExclamationTriangle,
  FaFileAlt,
  FaFileContract,
  FaFileSignature,
  FaIndustry,
  FaInfoCircle,
  FaMapMarkedAlt,
  FaMedal,
  FaRobot,
  FaSearch,
  FaShieldAlt,
  FaTasks,
  FaTools,
  FaUsers,
  FaUserTie
} from "react-icons/fa";

import {
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from "recharts";

import SupplierReadinessCard from "../components/Dashboard/SupplierReadinessCard";
import { useTranslate } from "../hooks/LanguageProvider";
import {
  DashboardSupplierProfile,
  DashboardFiliereStat,
  DashboardSimpleStat,
  DashboardStats,
  fetchDashboardStats
} from "../services/dashboard/api";
import { generateNationalDashboardPdf } from "../utilities/nationalReportPdf";

const COLORS = ["#0f766e", "#d97706", "#dc2626", "#2563eb", "#7c3aed", "#475569"];
const ICON_COLOR = "#c59b19";
const DASHBOARD_TOAST_ID = "dashboard-stats-toast";

const capacityColors = ["#22c55e", "#f59e0b", "#ef4444", "#3b82f6", "#8b5cf6", "#14b8a6"];

const getIcon = (icon: string, color = ICON_COLOR) => {
  const commonProps = {
    size: 20,
    style: { color }
  };

  switch (icon) {
    case "industry":
      return <FaIndustry {...commonProps} />;
    case "file":
      return <FaFileAlt {...commonProps} />;
    case "products":
      return <FaBoxes {...commonProps} />;
    case "message":
      return <FaEnvelope {...commonProps} />;
    case "response":
      return <FaFileSignature {...commonProps} />;
    case "check":
      return <FaCheckCircle {...commonProps} />;
    case "warning":
      return <FaExclamationTriangle {...commonProps} />;
    case "chart":
      return <FaChartLine {...commonProps} />;
    case "robot":
      return <FaRobot {...commonProps} />;
    case "cogs":
      return <FaCogs {...commonProps} />;
    case "database":
      return <FaDatabase {...commonProps} />;
    case "map":
      return <FaMapMarkedAlt {...commonProps} />;
    case "award":
      return <FaAward {...commonProps} />;
    case "shield":
      return <FaShieldAlt {...commonProps} />;
    case "certificate":
      return <FaFileContract {...commonProps} />;
    case "quality":
      return <FaMedal {...commonProps} />;
    case "users":
      return <FaUsers {...commonProps} />;
    case "oem":
      return <FaCar {...commonProps} />;
    case "organization":
      return <FaBuilding {...commonProps} />;
    case "rfq":
      return <FaClipboardList {...commonProps} />;
    case "rfq-check":
      return <FaClipboardCheck {...commonProps} />;
    case "tasks":
      return <FaTasks {...commonProps} />;
    default:
      return <FaFileAlt {...commonProps} />;
  }
};

const kpiIconStyles = [
  { match: "certification", color: "#0e7490", backgroundColor: "#cffafe" },
  { match: "iso", color: "#be123c", backgroundColor: "#ffe4e6" },
  { match: "iatf", color: "#be123c", backgroundColor: "#ffe4e6" },
  { match: "export", color: "#15803d", backgroundColor: "#dcfce7" },
  { match: "matching", color: "#4f46e5", backgroundColor: "#e0e7ff" },
  { match: "document", color: "#9333ea", backgroundColor: "#f3e8ff" },
  { match: "substitution", color: "#16a34a", backgroundColor: "#dcfce7" },
  { match: "produit", color: "#2563eb", backgroundColor: "#dbeafe" },
  { match: "rfq", color: "#7c3aed", backgroundColor: "#ede9fe" },
  { match: "oem", color: "#0891b2", backgroundColor: "#cffafe" },
  { match: "fournisseur", color: "#0f766e", backgroundColor: "#ccfbf1" },
  { match: "employ", color: "#d97706", backgroundColor: "#fef3c7" },
  { match: "capac", color: "#dc2626", backgroundColor: "#fee2e2" },
  { match: "wilaya", color: "#0284c7", backgroundColor: "#e0f2fe" }
];

const getKpiIconStyle = (label: string, index: number) => {
  const normalized = normalizeKpiLabel(label)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const style = kpiIconStyles.find((item) => normalized.includes(item.match));

  return style || {
    color: capacityColors[index % capacityColors.length],
    backgroundColor: "#f8fafc"
  };
};

const getKpiIconName = (label: string, fallbackIcon: string) => {
  const normalized = normalizeKpiLabel(label)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (normalized === "oem" || normalized.includes("constructeur")) return "oem";
  if (normalized.includes("certification")) return "certificate";
  if (normalized.includes("iso") || normalized.includes("iatf")) return "quality";
  if (normalized.includes("export")) return "award";
  if (normalized.includes("matching")) return "robot";
  if (normalized.includes("document")) return "warning";
  if (normalized.includes("substitution") || normalized.includes("import")) return "check";
  if (normalized.includes("reponse")) return "response";
  if (normalized.includes("ouverte")) return "rfq-check";
  if (normalized.includes("rfq")) return "rfq";
  if (normalized.includes("produit")) return "products";
  if (normalized.includes("fournisseur")) return "industry";
  if (normalized.includes("employ")) return "users";
  if (normalized.includes("capac")) return "cogs";
  if (normalized.includes("wilaya")) return "map";

  return fallbackIcon;
};

const InfoTip = ({ text }: { text: string }) => (
  <OverlayTrigger
    placement="top"
    overlay={<BootstrapTooltip>{text}</BootstrapTooltip>}
  >
    <span
      className="text-muted d-inline-flex align-items-center justify-content-center"
      style={{ cursor: "help", width: 28, height: 28 }}
    >
      <FaInfoCircle size={14} />
    </span>
  </OverlayTrigger>
);

const decodeEscapedUnicode = (value: string) =>
  value.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );

const normalizeKpiLabel = (label: string) => {
  const decodedLabel = decodeEscapedUnicode(label);
  const lower = decodedLabel.toLowerCase();

  if (lower === "suppliers") return "Fournisseurs";
  if (lower === "products" || lower === "produits declares" || lower === "produits references" || lower === "produits référencés") return "Produits référencés";
  if (lower === "open rfqs") return "RFQs ouvertes";
  if (lower === "import substitution") return "Substitution import";
  if (lower === "rfq responses") return "Reponses RFQ";
  if (lower === "matching results") return "Matchings IA";
  if (lower === "ai docs review") return "Documents IA a revoir";
  if (lower === "certifications") return "Certifications";
  if (
    lower === "employes embauches" ||
    lower === "employés embauchés" ||
    lower === "effectifs fournisseurs"
  ) return "Employés embauchés";
  if (lower === "capacite cumulee declaree" || lower === "capacité cumulée déclarée" || lower === "capacite cumulee" || lower === "capacité cumulée") return "Capacité cumulée";

  return decodedLabel;
};

const emptyDashboard: DashboardStats = {
  kpis: [
    { label: "Fournisseurs", value: 0, icon: "industry" },
    { label: "Produits r\u00e9f\u00e9renc\u00e9s", value: 0, icon: "file" },
    { label: "RFQs ouvertes", value: 0, icon: "message" },
    { label: "Substitution import", value: 0, icon: "check" }
  ],
  secondaryKpis: [
    { label: "OEM", value: 0, icon: "industry" },
    { label: "Employ\u00e9s embauch\u00e9s", value: 0, icon: "industry" },
    { label: "Capacit\u00e9 cumul\u00e9e", value: 0, icon: "chart" },
    { label: "Certifications", value: 0, icon: "shield" },
    { label: "Reponses RFQ", value: 0, icon: "message" },
    { label: "Matchings IA", value: 0, icon: "check" },
    { label: "Documents IA a revoir", value: 0, icon: "warning" }
  ],
  rfqActivity: [],
  readiness: [],
  industrialCapacity: [],
  aiMatching: {
    rfq: "Aucune RFQ",
    suppliers: 0,
    matchScore: 0,
    quality: "Non renseigne",
    bestCandidate: "Aucun fournisseur recommande",
    note: "Aucune donnee matching disponible",
    warning: "Aucune alerte matching"
  },
  databaseCoverage: [],
  rfqPipeline: [],
  topWilayas: [],
  topOemWilayas: [],
  supplierSegments: [],
  documentAiStatus: [],
  certificationStatus: [],
  certificationList: [],
  supplierProfiles: [],
  filiereStats: [],
  topProducts: [],
  activities: [],
  alerts: []
};

const kpiExplanations: Record<string, string> = {
  "Fournisseurs industriels recenses": "Base nationale des entreprises industrielles identifiees.",
  "Produits r\u00e9f\u00e9renc\u00e9s": "Produits et familles de produits references dans la base fournisseurs.",
  "Wilayas couvertes": "Couverture territoriale des fournisseurs industriels.",
  "Produits substituables a l'importation": "Produits importes pouvant etre produits localement.",
  "Capacite locale declaree / besoin estime": "Ratio entre capacite industrielle locale declaree et besoin industriel estime.",
  "Employ\u00e9s embauch\u00e9s": "Nombre total d'employes embauches par les fournisseurs industriels.",
  "Capacit\u00e9 cumul\u00e9e": "Somme des capacites annuelles referencees dans les produits fournisseurs.",
  "Fournisseurs export-ready": "Fournisseurs avec potentiel export ou references internationales.",
  "Fournisseurs ISO / IATF": "Fournisseurs disposant d'une certification qualite declaree ou verifiee.",
  Certifications: "Nombre total de certifications fournisseurs referencees.",
  "RFQ constructeurs actifs": "Besoins constructeurs en cours de qualification ou matching."
};

const normalizedKpiExplanations: Record<string, string> = {
  Fournisseurs: "Fournisseurs issus de la table suppliers, pas seulement des utilisateurs.",
  Produits: "Produits references dans supplier_products et rattaches a des fournisseurs.",
  "Produits r\u00e9f\u00e9renc\u00e9s": "Produits references dans supplier_products et rattaches a des fournisseurs.",
  "RFQs ouvertes": "Demandes constructeurs ouvertes et disponibles pour matching fournisseur.",
  "Substitution import": "Produits marques comme substituables a l'importation.",
  OEM: "Constructeurs et donneurs d'ordre recenses dans les profils OEM.",
  "Reponses RFQ": "Reponses fournisseurs soumises aux demandes constructeurs.",
  "Matchings IA": "Resultats de matching fournisseur/RFQ calcules par AIDA.",
  "Documents IA a revoir": "Documents analyses par IA necessitant une verification humaine.",
  Certifications: "Certifications fournisseurs referencees dans supplier_certifications.",
  "Employ\u00e9s embauch\u00e9s": "Total des employes embauches par les fournisseurs.",
  "Capacit\u00e9 cumul\u00e9e": "Somme des capacites annuelles referencees par produit."
};

const filiereFilters = [
  "Faisceaux & cablage",
  "Plasturgie",
  "Metallurgie",
  "Batteries",
  "Pneumatiques",
  "Electronique",
  "Verre automobile",
  "Textile automobile",
  "Chimie & lubrifiants",
  "Services industriels",
  "Machines agricoles",
  "Truck & bus",
  "Motocycles"
];

const industrialPoles = [
  "Alger",
  "Oran",
  "Setif",
  "Blida",
  "Batna",
  "Boumerdes",
  "Bordj Bou Arreridj",
  "Ain M'lila",
  "Tissemsilt"
];

const strongSectors = [
  "Metallurgie",
  "Plasturgie",
  "Batteries",
  "Services industriels",
  "Carrosserie",
  "Pneumatiques"
];

const weakSectors = [
  "Electronique embarquee",
  "Capteurs",
  "Connectique automobile",
  "Airbag",
  "ABS / freinage avance",
  "Pieces moteur critiques",
  "Outillage et moules",
  "Bancs de test"
];

const homologationSteps = [
  "Recensement",
  "Verification documentaire",
  "Visite technique",
  "Echantillon produit",
  "Test qualite",
  "APQP / PPAP",
  "Homologation constructeur",
  "Contrat industriel"
];

const maturityCriteria = [
  { name: "Capacite de production", value: 0 },
  { name: "Qualite", value: 0 },
  { name: "Certification", value: 0 },
  { name: "Experience constructeur", value: 0 },
  { name: "Equipements", value: 0 },
  { name: "Ressources humaines", value: 0 },
  { name: "Export", value: 0 },
  { name: "Tracabilite", value: 0 },
  { name: "Digitalisation", value: 0 },
  { name: "Integration locale", value: 0 }
];

const fallbackSupplierProfile: DashboardSupplierProfile = {
  idSupplier: 0,
  name: "Aucun fournisseur",
  wilaya: "",
  address: "",
  contact: "",
  sector: "Non renseigne",
  products: 0,
  machines: 0,
  certifications: 0,
  documents: 0,
  capacity: 0,
  employees: 0,
  material: "Non renseigne",
  maturity: "Non renseigne",
  supportNeed: "Non renseigne",
  status: "Non renseigne",
  score: 0
};

const substitutionRows: Array<{
  product: string;
  supplier: string;
  capacity: string;
  qualityGap: string;
  certificationGap: string;
  investment: string;
  gain: string;
  priority: string;
}> = [];

const numberValue = (value: number | string) => {
  if (typeof value === "number") return value;
  const parsed = Number(String(value).replace(/[^\d.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultRfqEndDate = () => toDateInputValue(new Date());

const normalizeWilayaName = (name: string) => {
  const trimmed = String(name || "").trim();
  const normalized = trimmed
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (!trimmed || ["algerie", "algeria", "dz"].includes(normalized)) {
    return "À confirmer";
  }

  if (normalized === "rouiba") {
    return "Alger";
  }

  return trimmed;
};

const normalizeWilayas = (items: DashboardSimpleStat[]) => {
  const grouped = new Map<string, number>();

  items.forEach((item) => {
    const name = normalizeWilayaName(item.name);
    if (!name) return;
    grouped.set(name, (grouped.get(name) || 0) + Number(item.value || 0));
  });

  return Array.from(grouped.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
};

export default function Dashboard() {
  const { translate } = useTranslate();
  const [dashboard, setDashboard] = useState<DashboardStats>(emptyDashboard);
  const [loading, setLoading] = useState<boolean>(true);
  const [institutionalMode, setInstitutionalMode] = useState(false);
  const [selectedFiliere, setSelectedFiliere] = useState("Faisceaux & cablage");
  const [showSupplierProfile, setShowSupplierProfile] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(null);
  const [rfqStartDate, setRfqStartDate] = useState("");
  const [rfqEndDate, setRfqEndDate] = useState("");
  const [appliedRfqStartDate, setAppliedRfqStartDate] = useState("");
  const [appliedRfqEndDate, setAppliedRfqEndDate] = useState("");
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const isAdmin = useMemo(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      return String(storedUser?.id_role || localStorage.getItem("id_role") || "") === "1";
    } catch {
      return String(localStorage.getItem("id_role") || "") === "1";
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setDashboardError(null);
    toast.dismiss(DASHBOARD_TOAST_ID);
    const toastId = toast.loading(translate("Chargement des donnees dashboard..."), {
      toastId: DASHBOARD_TOAST_ID,
      autoClose: false
    });

    const rfqParams =
      appliedRfqStartDate && appliedRfqEndDate
        ? {
            rfqStartDate: appliedRfqStartDate,
            rfqEndDate: appliedRfqEndDate
          }
        : undefined;

    fetchDashboardStats(rfqParams)
      .then((data) => {
        if (!mounted) return;

        if (!data) {
          const message = "Probleme de connexion. Impossible de charger les donnees dashboard pour le moment.";
          setDashboardError(message);
          toast.update(toastId, {
            render: translate(message),
            type: "error",
            isLoading: false,
            autoClose: 5000
          });
          return;
        }

        setDashboard({
          ...emptyDashboard,
          ...data,
          kpis: data.kpis?.length ? data.kpis : emptyDashboard.kpis,
          secondaryKpis: data.secondaryKpis?.length ? data.secondaryKpis : emptyDashboard.secondaryKpis,
          rfqActivity: data.rfqActivity || [],
          readiness: data.readiness || [],
          industrialCapacity: data.industrialCapacity || [],
          aiMatching: data.aiMatching || emptyDashboard.aiMatching,
          databaseCoverage: data.databaseCoverage || [],
          rfqPipeline: data.rfqPipeline || [],
          topWilayas: data.topWilayas ? normalizeWilayas(data.topWilayas) : [],
          topOemWilayas: data.topOemWilayas ? normalizeWilayas(data.topOemWilayas) : [],
          supplierSegments: data.supplierSegments || [],
          documentAiStatus: data.documentAiStatus || [],
          certificationStatus: data.certificationStatus || [],
          certificationList: data.certificationList || [],
          topProducts: data.topProducts || [],
          supplierProfiles: data.supplierProfiles || [],
          filiereStats: data.filiereStats || [],
          activities: data.activities || [],
          alerts: data.alerts || []
        });

        if (!appliedRfqStartDate && !appliedRfqEndDate && data.rfqActivityRange) {
          setRfqStartDate(data.rfqActivityRange.startDate);
          setRfqEndDate(data.rfqActivityRange.endDate);
          setAppliedRfqStartDate(data.rfqActivityRange.startDate);
          setAppliedRfqEndDate(data.rfqActivityRange.endDate);
        }

        toast.update(toastId, {
          render: translate("Donnees dashboard chargees depuis la base"),
          type: "success",
          isLoading: false,
          autoClose: 2500
        });
      })
      .catch(() => {
        if (!mounted) return;
        const message = "Probleme de connexion. Verifiez votre acces reseau puis reessayez.";
        setDashboardError(message);
        toast.update(toastId, {
          render: translate(message),
          type: "error",
          isLoading: false,
          autoClose: 5000
        });
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
      toast.dismiss(toastId);
    };
  }, [appliedRfqEndDate, appliedRfqStartDate, translate]);

  const {
    kpis,
    secondaryKpis,
    rfqActivity,
    rfqActivityBounds,
    readiness,
    industrialCapacity,
    aiMatching,
    databaseCoverage,
    rfqPipeline,
    topWilayas,
    topOemWilayas = [],
    supplierSegments,
    documentAiStatus,
    certificationStatus,
    certificationList = [],
    topProducts,
    supplierProfiles,
    filiereStats = [],
    activities,
    alerts
  } = dashboard;

  const supplierCount = useMemo(() => {
    const supplierKpi = kpis.find((kpi) => kpi.label.toLowerCase().includes("fournisseur") || kpi.label.toLowerCase().includes("supplier"));
    return supplierKpi ? numberValue(supplierKpi.value) : 0;
  }, [kpis]);

  const oemCount = useMemo(() => {
    const oemKpi = [...kpis, ...secondaryKpis].find((kpi) => normalizeKpiLabel(kpi.label).toLowerCase() === "oem");
    return oemKpi ? numberValue(oemKpi.value) : 0;
  }, [kpis, secondaryKpis]);

  const completedSegments = useMemo(() => {
    return supplierSegments;
  }, [supplierSegments]);

  const maxWilayaValue = useMemo(
    () => Math.max(1, ...topWilayas.map((item) => Number(item.value || 0))),
    [topWilayas]
  );

  const wilayaSupplierTotal = useMemo(
    () => topWilayas.reduce((sum, item) => sum + Number(item.value || 0), 0),
    [topWilayas]
  );

  const maxOemWilayaValue = useMemo(
    () => Math.max(1, ...topOemWilayas.map((item) => Number(item.value || 0))),
    [topOemWilayas]
  );

  const wilayaOemTotal = useMemo(
    () => topOemWilayas.reduce((sum, item) => sum + Number(item.value || 0), 0),
    [topOemWilayas]
  );

  const selectedSupplier = useMemo(() => {
    if (!supplierProfiles.length) return fallbackSupplierProfile;
    return (
      supplierProfiles.find((supplier) => supplier.idSupplier === selectedSupplierId) ||
      supplierProfiles[0]
    );
  }, [selectedSupplierId, supplierProfiles]);

  const supplierProofs = useMemo(
    () => [
      { name: "Produits r\u00e9f\u00e9renc\u00e9s", value: selectedSupplier.products, status: selectedSupplier.products > 0 ? "Valide" : "A verifier" },
      { name: "Machines principales", value: selectedSupplier.machines, status: selectedSupplier.machines > 0 ? "Valide" : "A verifier" },
      { name: "Certifications ISO / IATF", value: selectedSupplier.certifications, status: selectedSupplier.certifications > 0 ? "Valide" : "A verifier" },
      { name: "Documents fournisseurs", value: selectedSupplier.documents, status: selectedSupplier.documents > 0 ? "Valide" : "A verifier" },
      { name: "Capacite declaree", value: selectedSupplier.capacity, status: selectedSupplier.capacity > 0 ? "Valide" : "A verifier" },
      { name: "Effectifs declares", value: selectedSupplier.employees, status: selectedSupplier.employees > 0 ? "Valide" : "A verifier" }
    ],
    [selectedSupplier]
  );

  const normalizeSectorName = (value: string) =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/&/g, " ")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

  const availableFiliereFilters = useMemo(() => {
    const names = new Set<string>();

    filiereStats.forEach((item) => {
      if (item.name) names.add(item.name);
    });

    filiereFilters.forEach((item) => names.add(item));

    return Array.from(names);
  }, [filiereStats]);

  const selectedFiliereStats = useMemo<DashboardFiliereStat>(() => {
    const selected = normalizeSectorName(selectedFiliere);
    const selectedFirstWord = selected.split(" ")[0] || selected;

    const exact = filiereStats.find(
      (item) => normalizeSectorName(item.name) === selected
    );

    const partial =
      exact ||
      filiereStats.find((item) => {
        const name = normalizeSectorName(item.name);
        return name.includes(selectedFirstWord) || selected.includes(name.split(" ")[0]);
      });

    if (partial) {
      return {
        name: partial.name,
        suppliers: Number(partial.suppliers || 0),
        products: Number(partial.products || 0),
        capacity: Number(partial.capacity || 0),
        poles: partial.poles?.length ? partial.poles : industrialPoles.slice(0, 4),
      };
    }

    return {
      name: selectedFiliere,
      suppliers: 0,
      products: 0,
      capacity: 0,
      poles: [],
    };
  }, [filiereStats, selectedFiliere]);

  const rfqActivityTotal = useMemo(
    () => rfqActivity.reduce((sum, item) => sum + Number(item.rfq || 0), 0),
    [rfqActivity]
  );

  const hasRfqsOutsideAppliedRange =
    rfqActivityTotal === 0 && Number(rfqActivityBounds?.totalRfqs || 0) > 0;

  const rfqDateFilterChanged =
    rfqStartDate !== appliedRfqStartDate || rfqEndDate !== appliedRfqEndDate;

  const applyRfqDateFilter = () => {
    if (!rfqStartDate || !rfqEndDate) return;
    setAppliedRfqStartDate(rfqStartDate);
    setAppliedRfqEndDate(rfqEndDate);
  };

  const applyQuickRfqRange = (days: number) => {
    const rangeEndDate = new Date();
    const date = new Date(rangeEndDate);
    date.setDate(date.getDate() - (days - 1));
    const start = toDateInputValue(date);
    const end = toDateInputValue(rangeEndDate);

    setRfqStartDate(start);
    setRfqEndDate(end);
    setAppliedRfqStartDate(start);
    setAppliedRfqEndDate(end);
  };

  const applyAllRfqRange = () => {
    const start = rfqActivityBounds?.startDate || "2000-01-01";
    const end = rfqActivityBounds?.endDate || getDefaultRfqEndDate();

    setRfqStartDate(start);
    setRfqEndDate(end);
    setAppliedRfqStartDate(start);
    setAppliedRfqEndDate(end);
  };

  const strongestCapacity = useMemo(() => {
    if (!industrialCapacity.length) return null;
    return industrialCapacity.reduce((best, item) => Number(item.value) > Number(best.value) ? item : best, industrialCapacity[0]);
  }, [industrialCapacity]);

  const institutionalKpis = useMemo(() => {
    const firstKpis = [...kpis, ...secondaryKpis];
    return [
      firstKpis.find((item) => item.label.toLowerCase().includes("fournisseur")) || emptyDashboard.kpis[0],
      firstKpis.find((item) => item.label.toLowerCase().includes("wilaya")) || emptyDashboard.kpis[2],
      firstKpis.find((item) => item.label.toLowerCase().includes("produit")) || emptyDashboard.kpis[1],
      firstKpis.find((item) => item.label.toLowerCase().includes("capacite")) || emptyDashboard.secondaryKpis[0],
      firstKpis.find((item) => item.label.toLowerCase().includes("substituable") || item.label.toLowerCase().includes("import")) || emptyDashboard.kpis[3],
      firstKpis.find((item) => item.label.toLowerCase().includes("export")) || emptyDashboard.secondaryKpis[1],
      firstKpis.find((item) => item.label.toLowerCase().includes("iso") || item.label.toLowerCase().includes("iatf")) || emptyDashboard.secondaryKpis[2]
    ];
  }, [kpis, secondaryKpis]);

  const getActivityColor = (type: string) => {
    switch (type) {
      case "RFQ":
        return "primary";
      case "Response":
        return "success";
      case "Alert":
        return "warning";
      case "Supplier":
        return "info";
      case "Product":
        return "dark";
      default:
        return "secondary";
    }
  };

  const getAlertStyle = (type: string) => {
    switch (type) {
      case "high":
        return { bg: "#fee2e2", color: "#dc2626" };
      case "medium":
        return { bg: "#fef3c7", color: "#b45309" };
      default:
        return { bg: "#e2e8f0", color: "#475569" };
    }
  };

  const exportReport = (label: string) => {
    document.title = `AIDA - ${label}`;
    window.print();
  };

  const exportNationalPdf = () => {
    if (!isAdmin) {
      toast.warn(translate("Export PDF reserve aux administrateurs"));
      return;
    }

    try {
      generateNationalDashboardPdf(dashboard);
      toast.success(translate("Rapport national PDF genere"));
    } catch (error) {
      console.error("Erreur generation rapport national PDF:", error);
      toast.error(translate("Impossible de generer le rapport national PDF"));
    }
  };

  const getKpiLink = (label: string) => {
    const normalized = normalizeKpiLabel(label)
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    if (normalized === "oem" || normalized.includes("constructeur")) return "/OEMs";
    if (normalized === "fournisseurs" || normalized === "suppliers" || normalized.includes("fournisseurs industriels")) return "/Suppliers";

    return "";
  };

  if (institutionalMode) {
    return (
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
          <div>
            <Badge bg="dark" className="mb-2">{translate("Vue institutionnelle")}</Badge>
            <h3 className="fw-bold mb-1">{translate("AIDA - Cartographie nationale de l'integration automobile")}</h3>
            <p className="text-muted mb-0" style={{ maxWidth: 980 }}>
              {translate("AIDA est une plateforme nationale dediee a l'identification, la qualification et le developpement des capacites algeriennes de sous-traitance industrielle, au service de l'integration locale, de la substitution a l'importation et de la construction d'une chaine de valeur automobile nationale.")}
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-dark" size="sm" onClick={() => setInstitutionalMode(false)}>
              {translate("Dashboard operationnel")}
            </Button>
            <Button variant="dark" size="sm" onClick={() => exportReport("Rapport institutionnel")}>
              <FaDownload className="me-2" /> {translate("Export PDF officiel")}
            </Button>
          </div>
        </div>

        <Row className="g-3 mb-4">
          {institutionalKpis.map((kpi, index) => {
            const kpiLink = getKpiLink(kpi.label);
            const card = (
              <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8, cursor: kpiLink ? "pointer" : "default" }}>
                <Card.Body>
                  <div className="d-flex align-items-center gap-3 mb-2">
                    <div
                      className="p-3 rounded-3"
                      style={{ backgroundColor: getKpiIconStyle(kpi.label, index).backgroundColor }}
                    >
                      {getIcon(getKpiIconName(kpi.label, kpi.icon), getKpiIconStyle(kpi.label, index).color)}
                    </div>
                    <div>
                      <div className="fs-3 fw-bold">{kpi.value}</div>
                      <div className="fw-semibold">{translate(normalizeKpiLabel(kpi.label))}</div>
                    </div>
                  </div>
                  <small className="text-muted">
                    {translate(kpiExplanations[normalizeKpiLabel(kpi.label)] || kpiExplanations[kpi.label] || "Indicateur consolide pour lecture institutionnelle.")}
                  </small>
                </Card.Body>
              </Card>
            );

            return (
              <Col lg={3} md={6} key={`${kpi.label}-${index}`}>
                {kpiLink ? (
                  <a href={kpiLink} className="text-decoration-none text-reset d-block h-100">
                    {card}
                  </a>
                ) : card}
              </Col>
            );
          })}
        </Row>

        <Row className="g-3 mb-4">
          <Col lg={6}>
            <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
              <Card.Body>
                <h6 className="fw-bold mb-3">{translate("Filieres fortes")}</h6>
                <div className="d-flex flex-wrap gap-2">
                  {strongSectors.map((sector) => (
                    <Badge key={sector} bg="success" className="px-3 py-2">{translate(sector)}</Badge>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6}>
            <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
              <Card.Body>
                <h6 className="fw-bold mb-3">{translate("Filieres a developper")}</h6>
                <div className="d-flex flex-wrap gap-2">
                  {weakSectors.map((sector) => (
                    <Badge key={sector} bg="warning" text="dark" className="px-3 py-2">{translate(sector)}</Badge>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3">
          <Col lg={7}>
            <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0">{translate("Matching constructeur national")}</h6>
                  <Badge bg="success">{translate("Couverture locale estimee")} {aiMatching.matchScore}%</Badge>
                </div>
                <Row className="g-3">
                  <Col md={3}><div className="text-muted small">{translate("Fournisseurs disponibles")}</div><div className="fs-4 fw-bold">3</div></Col>
                  <Col md={3}><div className="text-muted small">{translate("A qualifier")}</div><div className="fs-4 fw-bold">2</div></Col>
                  <Col md={3}><div className="text-muted small">{translate("Capacite insuffisante")}</div><div className="fs-4 fw-bold">1</div></Col>
                  <Col md={3}><div className="text-muted small">{translate("Homologues")}</div><div className="fs-4 fw-bold">0</div></Col>
                </Row>
                <ProgressBar now={aiMatching.matchScore} variant="success" className="mt-3" />
                <div className="small text-muted mt-2">{aiMatching.note}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={5}>
            <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
              <Card.Body>
                <h6 className="fw-bold mb-3">{translate("Exports officiels")}</h6>
                <div className="d-grid gap-2">
                  {["Rapport national PDF", "Fiche fournisseur PDF", "Cartographie wilaya PDF", "Liste fournisseurs Excel", "Rapport constructeur"].map((item) => (
                    <Button key={item} variant="outline-dark" size="sm" onClick={() => exportReport(item)}>
                      <FaDownload className="me-2" /> {translate("Exporter")} {translate(item)}
                    </Button>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
        <div>
          <h3 className="fw-bold mb-1">{translate("AIDA - Pilotage de l'integration automobile")}</h3>
          <p className="text-muted mb-0" style={{ maxWidth: 1000 }}>
            {translate("AIDA est une plateforme nationale dediee a l'identification, la qualification et le developpement des capacites algeriennes de sous-traitance industrielle, au service de l'integration locale, de la substitution a l'importation et de la construction d'une chaine de valeur automobile nationale.")}
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2 justify-content-end">
          {isAdmin && (
            <Button variant="outline-dark" size="sm" onClick={exportNationalPdf}>
              <FaDownload className="me-2" /> {translate("Exporter rapport national PDF")}
            </Button>
          )}
        </div>
      </div>

      {loading && <div className="mb-3 small text-muted">{translate("Chargement des donnees dashboard...")}</div>}
      {dashboardError && (
        <div className="alert alert-warning py-2 small" role="alert">
          {dashboardError}
        </div>
      )}

      <Row className="g-3 mb-4">
        {[...kpis, ...secondaryKpis].map((kpi, index) => {
          const kpiLink = getKpiLink(kpi.label);
          const card = (
            <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8, cursor: kpiLink ? "pointer" : "default" }}>
              <Card.Body
                className="d-flex align-items-center justify-content-between gap-3 py-3"
                style={{ minHeight: 82 }}
              >
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="p-2 rounded-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: 48,
                      height: 48,
                      backgroundColor: getKpiIconStyle(kpi.label, index).backgroundColor
                    }}
                  >
                    {getIcon(getKpiIconName(kpi.label, kpi.icon), getKpiIconStyle(kpi.label, index).color)}
                  </div>
                  <div>
                    <h4 className="fw-bold mb-0">{kpi.value}</h4>
                    <div className="small fw-semibold">{translate(normalizeKpiLabel(kpi.label))}</div>
                  </div>
                </div>
                <InfoTip text={translate(normalizedKpiExplanations[normalizeKpiLabel(kpi.label)] || kpiExplanations[kpi.label] || "Indicateur consolide depuis les tables metier AIDA.")} />
              </Card.Body>
            </Card>
          );

          return (
            <Col xl={3} md={6} key={`${kpi.label}-${index}`}>
              {kpiLink ? (
                <a href={kpiLink} className="text-decoration-none text-reset d-block h-100">
                  {card}
                </a>
              ) : card}
            </Col>
          );
        })}
      </Row>

      <Row className="g-3 mb-4">
        <Col md={8}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                <h6 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                  {translate("Activite RFQ constructeur")}
                  <InfoTip text={translate("Le graphe affiche les RFQ creees dans la periode selectionnee. Si la courbe est a zero, aucune RFQ n'a ete creee sur ces dates.")} />
                </h6>
                <Badge bg="light" text="dark" className="border">
                  {rfqActivityTotal} {translate("RFQ sur la periode appliquee")}
                </Badge>
              </div>

              <div className="d-flex flex-wrap align-items-end gap-2 p-2 mb-3 rounded-3" style={{ backgroundColor: "#f8fafc" }}>
                <div className="me-2">
                  <div className="small fw-semibold">{translate("Filtre par date RFQ")}</div>
                  <div className="small text-muted">
                    {translate("Applique")}: {appliedRfqStartDate} {translate("au")} {appliedRfqEndDate}
                  </div>
                </div>
                <Form.Group>
                  <Form.Label className="small text-muted mb-1">{translate("Du")}</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={rfqStartDate}
                    onChange={(event) => setRfqStartDate(event.target.value)}
                    style={{ width: 145 }}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label className="small text-muted mb-1">{translate("Au")}</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={rfqEndDate}
                    onChange={(event) => setRfqEndDate(event.target.value)}
                    style={{ width: 145 }}
                  />
                </Form.Group>
                <Button
                  variant={rfqDateFilterChanged ? "dark" : "outline-secondary"}
                  size="sm"
                  onClick={applyRfqDateFilter}
                  disabled={!rfqDateFilterChanged || loading || !rfqStartDate || !rfqEndDate}
                >
                  {translate("Appliquer")}
                </Button>
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={() => applyQuickRfqRange(30)}
                  disabled={loading}
                >
                  {translate("30 jours")}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => applyQuickRfqRange(90)}
                  disabled={loading}
                >
                  {translate("90 jours")}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={applyAllRfqRange}
                  disabled={loading}
                >
                  {translate("Tous")}
                </Button>
              </div>
              {rfqActivityTotal === 0 ? (
                <div className="d-flex align-items-center justify-content-center text-center bg-light rounded-3" style={{ height: 250 }}>
                  <div>
                    <div className="fw-semibold">{translate("Aucune RFQ creee sur cette periode")}</div>
                    {hasRfqsOutsideAppliedRange ? (
                      <>
                        <small className="text-muted d-block">
                          {translate("RFQ disponibles")}: {rfqActivityBounds?.startDate} {translate("au")} {rfqActivityBounds?.endDate}
                        </small>
                        <Button
                          variant="outline-dark"
                          size="sm"
                          className="mt-2"
                          onClick={applyAllRfqRange}
                          disabled={loading}
                        >
                          {translate("Afficher la periode avec RFQ")}
                        </Button>
                      </>
                    ) : (
                      <small className="text-muted">{translate("Elargissez le filtre date ou creez une nouvelle RFQ pour alimenter ce graphe.")}</small>
                    )}
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={rfqActivity}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis allowDecimals={false} />
                    <RechartsTooltip />
                    <Line type="monotone" dataKey="rfq" stroke="#0f766e" strokeWidth={3} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <SupplierReadinessCard readiness={readiness} />
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">{translate("Capacite industrielle declaree")}</h6>
                <small className="text-muted">{translate("Repartition de la capacite declaree par filiere")}</small>
              </div>
              {industrialCapacity.map((item, index) => (
                <div key={index} className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span className="fw-medium">{item.name}</span>
                    <span className="fw-bold" style={{ color: item.color || capacityColors[index % capacityColors.length] }}>
                      {item.value}%
                    </span>
                  </div>
                  <div
                    className="rounded-pill"
                    style={{
                      height: 8,
                      backgroundColor: "#e5e7eb",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      className="h-100 rounded-pill"
                      style={{
                        width: `${Math.min(100, Number(item.value || 0))}%`,
                        backgroundColor: item.color || capacityColors[index % capacityColors.length],
                      }}
                    />
                  </div>
                  <small className="text-muted">{item.count || 0} {translate("produit(s)")} - {translate("capacite declaree")}: {Number(item.capacity || 0).toLocaleString("fr-FR")}</small>
                </div>
              ))}
              <div className="mt-3 pt-2 border-top">
                <small className="text-muted">{translate("Filiere avec la plus forte capacite declaree")}</small>
                <div
                  className="fw-bold"
                  style={{ color: strongestCapacity?.color || capacityColors[0] }}
                >
                  {strongestCapacity ? `${strongestCapacity.name} (${strongestCapacity.value}%)` : translate("Non renseigne")}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">{translate("RFQ constructeur + matching automatique")}</h6>
                <small className="text-muted">{translate("Besoin constructeur vers fournisseurs locaux")}</small>
              </div>
              <Row className="text-center mb-3">
                <Col><small className="text-muted">{translate("Produit recherche")}</small><div className="fw-bold fs-6">{aiMatching.rfq}</div></Col>
                <Col><small className="text-muted">{translate("Fournisseurs detectes")}</small><div className="fw-bold fs-5">{aiMatching.suppliers}</div></Col>
                <Col><small className="text-muted">{translate("Couverture locale")}</small><div className="fw-bold fs-5 text-success">{aiMatching.matchScore}%</div></Col>
              </Row>
              <ProgressBar now={aiMatching.matchScore} variant="success" className="mb-3" />
              <Row className="g-2 mb-3">
                <Col xs={6}><div className="p-2 bg-light rounded-3"><div className="fw-bold">3</div><small>{translate("Fournisseurs disponibles")}</small></div></Col>
                <Col xs={6}><div className="p-2 bg-light rounded-3"><div className="fw-bold">2</div><small>{translate("Fournisseurs a qualifier")}</small></div></Col>
                <Col xs={6}><div className="p-2 bg-light rounded-3"><div className="fw-bold">1</div><small>{translate("Capacite insuffisante")}</small></div></Col>
                <Col xs={6}><div className="p-2 bg-light rounded-3"><div className="fw-bold">0</div><small>{translate("Homologue actuellement")}</small></div></Col>
              </Row>
              <div className="p-2 rounded-3 bg-light mb-3">
                <small className="text-muted">{translate("Meilleur candidat")}</small>
                <div className="fw-semibold">{aiMatching.bestCandidate}</div>
                <small className="text-muted">{aiMatching.note}</small>
              </div>
              <div className="d-flex justify-content-between gap-2">
                <Button variant="outline-dark" size="sm"><FaSearch className="me-2" /> {translate("Voir RFQ")}</Button>
                <Button variant="success" size="sm">{translate("Comparer fournisseurs")}</Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col lg={8}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">{translate("Database Coverage")}</h6>
                <small className="text-muted">{translate("Modules alimentes par la base")}</small>
              </div>

              <Table responsive hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>{translate("Module")}</th>
                    <th className="text-end">{translate("Count")}</th>
                    <th>{translate("Detail")}</th>
                  </tr>
                </thead>
                <tbody>
                  {databaseCoverage.length ? (
                    databaseCoverage.map((item, index) => (
                      <tr key={`${item.module}-${index}`}>
                        <td className="fw-semibold">{item.module}</td>
                        <td className="text-end fw-bold">{item.count}</td>
                        <td className="text-muted small">{item.detail}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="text-muted small">{translate("Aucune donnee disponible")}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">{translate("RFQ Pipeline")}</h6>
                <small className="text-muted">{translate("Statuts constructeur")}</small>
              </div>

              <ResponsiveContainer width="100%" height={210}>
                <PieChart>
                  <Pie
                    data={rfqPipeline}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                  >
                    {rfqPipeline.map((entry, index) => (
                      <Cell key={`${entry.name}-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>

              <div className="d-flex flex-column gap-1 mt-2">
                {rfqPipeline.length ? (
                  rfqPipeline.map((item) => (
                    <div key={item.name} className="d-flex justify-content-between small">
                      <span className="text-muted">{item.name}</span>
                      <span className="fw-bold">{item.value}</span>
                    </div>
                  ))
                ) : (
                  <small className="text-muted">{translate("Aucune RFQ")}</small>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col lg={7}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">{translate("Fiche fournisseur AIDA")}</h6>
                <Badge bg="success">Score AIDA {selectedSupplier.score}/100</Badge>
              </div>
              <div className="d-flex justify-content-between align-items-start gap-3">
                <div>
                  <h5 className="fw-bold mb-1">{selectedSupplier.name}</h5>
                  <div className="text-muted small">{selectedSupplier.wilaya || translate("Wilaya non renseignee")} - {translate(selectedSupplier.sector)}</div>
                  <div className="small mt-2">{translate("Statut")}: <Badge bg="info">{translate(selectedSupplier.status)}</Badge></div>
                  <div className="small mt-2">{translate("Action recommandee")}: {translate(selectedSupplier.supportNeed)}</div>
                </div>
                <Button variant="outline-dark" size="sm" onClick={() => setShowSupplierProfile(true)}>
                  {translate("Ouvrir la fiche complete")}
                </Button>
              </div>
              <ProgressBar now={selectedSupplier.score} variant="success" className="my-3" />
              <div className="d-flex flex-column gap-2 mb-3" style={{ maxHeight: 210, overflowY: "auto" }}>
                {supplierProfiles.length ? (
                  supplierProfiles.map((supplier) => (
                    <button
                      key={supplier.idSupplier}
                      type="button"
                      className={`btn btn-sm text-start ${supplier.idSupplier === selectedSupplier.idSupplier ? "btn-dark" : "btn-outline-secondary"}`}
                      onClick={() => setSelectedSupplierId(supplier.idSupplier)}
                    >
                      <span className="fw-semibold">{supplier.name}</span>
                      <span className="ms-2 small">{supplier.wilaya || "-"} / {supplier.score}/100 / {supplier.employees} {translate("employe(s)")}</span>
                    </button>
                  ))
                ) : (
                  <div className="small text-muted bg-light rounded-3 p-2">
                    {translate("Aucun fournisseur disponible pour le moment.")}
                  </div>
                )}
              </div>
              <Row className="g-2">
                {maturityCriteria.slice(0, 6).map((criterion) => (
                  <Col md={6} key={criterion.name}>
                    <div className="d-flex justify-content-between small mb-1">
                      <span>{translate(criterion.name)}</span>
                      <strong>{criterion.value}/100</strong>
                    </div>
                    <ProgressBar now={criterion.value} style={{ height: 6 }} />
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={5}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <h6 className="fw-semibold mb-3">{translate("Parcours d'homologation constructeur")}</h6>
              <div className="d-flex flex-column gap-2">
                {homologationSteps.map((step, index) => (
                  <div key={step} className="d-flex align-items-center gap-2">
                    <Badge bg={index < 3 ? "success" : "secondary"}>{index + 1}</Badge>
                    <span className="small fw-medium">{translate(step)}</span>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col lg={5}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                  {translate("RFQs constructeur")}
                  <InfoTip text={translate("Vue rapide des demandes constructeurs: besoin, matching fournisseur et action a lancer.")} />
                </h6>
                <Badge bg="dark">{rfqPipeline.reduce((sum, item) => sum + Number(item.value || 0), 0)} RFQs</Badge>
              </div>

              <div className="d-flex flex-column gap-3">
                <div className="p-3 rounded-3 border">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <div className="fw-bold">{aiMatching.rfq}</div>
                      <small className="text-muted">{translate("Besoin constructeur prioritaire")}</small>
                    </div>
                    <Badge bg={aiMatching.matchScore >= 60 ? "success" : "warning"}>{aiMatching.matchScore}% {translate("couverture")}</Badge>
                  </div>
                  <ProgressBar now={aiMatching.matchScore} variant={aiMatching.matchScore >= 60 ? "success" : "warning"} className="my-3" style={{ height: 7 }} />
                  <Row className="g-2 text-center">
                    <Col xs={4}><div className="fw-bold">{aiMatching.suppliers}</div><small className="text-muted">{translate("Detectes")}</small></Col>
                    <Col xs={4}><div className="fw-bold">2</div><small className="text-muted">{translate("A qualifier")}</small></Col>
                    <Col xs={4}><div className="fw-bold">0</div><small className="text-muted">{translate("Homologues")}</small></Col>
                  </Row>
                </div>

                <Table responsive size="sm" className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>{translate("RFQ")}</th>
                      <th>{translate("Statut")}</th>
                      <th className="text-end">{translate("Volume")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rfqPipeline.length ? (
                      rfqPipeline.slice(0, 4).map((item) => (
                        <tr key={item.name}>
                          <td className="fw-semibold">RFQ {item.name}</td>
                          <td><Badge bg="light" text="dark">{item.name}</Badge></td>
                          <td className="text-end fw-bold">{item.value}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={3} className="text-muted small">{translate("Aucune RFQ disponible")}</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="mt-3 d-flex gap-2">
                <Button variant="dark" size="sm" href="/rfq/add">
                  <FaClipboardList className="me-2" /> {translate("Nouvelle RFQ")}
                </Button>
                <Button variant="outline-dark" size="sm" href="/rfqs">{translate("Voir les RFQs")}</Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">{translate("Substitution a l'importation")}</h6>
                <small className="text-muted">{translate("Priorisation industrielle")}</small>
              </div>
              <Table responsive hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>{translate("Produit")}</th>
                    <th>{translate("Fournisseur local potentiel")}</th>
                    <th>{translate("Capacite")}</th>
                    <th>{translate("Ecart")}</th>
                    <th>{translate("Investissement")}</th>
                    <th>{translate("Priorite")}</th>
                  </tr>
                </thead>
                <tbody>
                  {substitutionRows.map((row) => (
                    <tr key={row.product}>
                      <td className="fw-semibold">{row.product}</td>
                      <td className="small">{row.supplier}</td>
                      <td className="small">{row.capacity}</td>
                      <td className="small">{row.qualityGap} / {row.certificationGap}</td>
                      <td className="small">{row.investment}</td>
                      <td><Badge bg={row.priority === "Haute" ? "danger" : "warning"}>{row.priority}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col lg={3}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">{translate("Wilayas fournisseurs")}</h6>
                <small className={wilayaSupplierTotal === supplierCount ? "text-success fw-semibold" : "text-danger fw-semibold"}>
                  {wilayaSupplierTotal}/{supplierCount} {translate("fournisseur(s)")}
                </small>
              </div>
              <div className="d-flex flex-column gap-2 pe-1" style={{ maxHeight: 420, overflowY: "auto" }}>
                {topWilayas.map((item, index) => (
                  <div key={`${item.name}-${index}`}>
                    <div className="d-flex justify-content-between mb-1">
                      <small className="fw-medium">{item.name}</small>
                      <small className="fw-bold">{item.value}</small>
                    </div>
                    <div className="progress" style={{ height: 7, backgroundColor: "#e5e7eb" }}>
                      <div
                        className="progress-bar"
                        style={{
                          width: `${Math.max(4, Math.round((Number(item.value || 0) / maxWilayaValue) * 100))}%`,
                          backgroundColor: COLORS[index % COLORS.length]
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">{translate("Wilayas OEM")}</h6>
                <small className={wilayaOemTotal === oemCount ? "text-success fw-semibold" : "text-danger fw-semibold"}>
                  {wilayaOemTotal}/{oemCount} OEM
                </small>
              </div>
              <div className="d-flex flex-column gap-2 pe-1" style={{ maxHeight: 420, overflowY: "auto" }}>
                {topOemWilayas.length ? (
                  topOemWilayas.map((item, index) => (
                    <div key={`${item.name}-${index}`}>
                      <div className="d-flex justify-content-between mb-1">
                        <small className="fw-medium">{item.name}</small>
                        <small className="fw-bold">{item.value}</small>
                      </div>
                      <div className="progress" style={{ height: 7, backgroundColor: "#e5e7eb" }}>
                        <div
                          className="progress-bar"
                          style={{
                            width: `${Math.max(4, Math.round((Number(item.value || 0) / maxOemWilayaValue) * 100))}%`,
                            backgroundColor: COLORS[(index + 2) % COLORS.length]
                          }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <small className="text-muted">{translate("Aucune donnee OEM disponible")}</small>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <h6 className="fw-semibold mb-3">{translate("Segments fournisseurs")}</h6>
              <div className="d-flex flex-column gap-2">
                {completedSegments.map((item) => (
                  <div key={item.name} className="d-flex justify-content-between align-items-center p-2 rounded-3" style={{ backgroundColor: "#f8fafc" }}>
                    <span className="small fw-medium">{translate(item.name)}</span>
                    <Badge bg="dark">{item.value}</Badge>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                  {translate("Filieres & poles")}
                  <InfoTip text={translate("Filtre de lecture pour rapprocher filieres industrielles, fournisseurs et poles territoriaux.")} />
                </h6>
                <FaMapMarkedAlt className="text-muted" />
              </div>
              <Form.Select size="sm" className="mb-3" value={selectedFiliere} onChange={(event) => setSelectedFiliere(event.target.value)}>
                {availableFiliereFilters.map((item) => <option key={item} value={item}>{translate(item)}</option>)}
              </Form.Select>

              <Row className="g-2 mb-3">
                <Col xs={4}>
                  <div className="p-2 rounded-3 bg-light text-center">
                    <div className="fw-bold">{selectedFiliereStats.suppliers}</div>
                    <small className="text-muted">{translate("Fournisseurs")}</small>
                  </div>
                </Col>
                <Col xs={4}>
                  <div className="p-2 rounded-3 bg-light text-center">
                    <div className="fw-bold">{selectedFiliereStats.products}</div>
                    <small className="text-muted">{translate("Produits")}</small>
                  </div>
                </Col>
                <Col xs={4}>
                  <div className="p-2 rounded-3 bg-light text-center">
                    <div className="fw-bold">{selectedFiliereStats.capacity.toLocaleString("fr-FR")}</div>
                    <small className="text-muted">{translate("Capacite")}</small>
                  </div>
                </Col>
              </Row>

              <div className="small text-muted mb-2">{translate("Poles industriels lies a la cartographie")}</div>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {selectedFiliereStats.poles.length ? (
                  selectedFiliereStats.poles.map((pole, index) => (
                    <Badge key={pole} bg="light" text="dark" className="border px-2 py-2">
                      <span
                        className="d-inline-block rounded-circle me-1"
                        style={{ width: 8, height: 8, backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      {pole}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted small">{translate("Aucun pole rattache pour cette filiere")}</span>
                )}
              </div>

              <Button variant="outline-dark" size="sm" className="w-100" href="/national-cartography">
                {translate("Ouvrir la cartographie nationale")}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col lg={8}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">{translate("Produits référencés")}</h6>
                <small className="text-muted">{translate("Produits, capacites et preuves visuelles")}</small>
              </div>
              <Table responsive hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>{translate("Produit")}</th>
                    <th>{translate("Fournisseur")}</th>
                    <th>{translate("Categorie")}</th>
                    <th className="text-end">{translate("Capacite")}</th>
                    <th className="text-end">{translate("Photos")}</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((product) => (
                    <tr key={product.idProduct}>
                      <td className="fw-semibold">{translate(product.name)}</td>
                      <td className="small text-muted">{product.supplier}</td>
                      <td><Badge bg="light" text="dark">{translate(product.category)}</Badge></td>
                      <td className="text-end small">{product.annualCapacity} {product.unit}</td>
                      <td className="text-end fw-bold">{product.imagesCount}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <h6 className="fw-semibold mb-3">{translate("Preuves industrielles")}</h6>
              <div className="d-flex flex-column gap-2">
                {documentAiStatus.map((item) => (
                  <div key={item.name} className="d-flex justify-content-between align-items-center p-2 rounded-3" style={{ backgroundColor: "#f8fafc" }}>
                    <span className="small fw-medium">{translate(item.name)}</span>
                    <Badge bg={item.name.toLowerCase().includes("valide") ? "success" : item.name.toLowerCase().includes("non") ? "danger" : "warning"}>{item.value}</Badge>
                  </div>
                ))}
              </div>
              <h6 className="fw-semibold my-3">{translate("certifications")}</h6>
              <div className="d-flex flex-column gap-2">
                {certificationStatus.map((item) => (
                  <div key={item.name} className="d-flex justify-content-between align-items-center p-2 rounded-3" style={{ backgroundColor: "#f8fafc" }}>
                    <span className="small fw-medium">{translate(item.name)}</span>
                    <Badge bg="secondary">{item.value}</Badge>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col lg={12}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">{translate("certifications")}</h6>
                <small className="text-muted">{translate("Liste des certifications fournisseurs")}</small>
              </div>
              <Table responsive hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>{translate("certification")}</th>
                    <th>{translate("Fournisseur")}</th>
                    <th>{translate("Numero")}</th>
                    <th>{translate("Produit lie")}</th>
                    <th>{translate("Expiration")}</th>
                    <th>{translate("status")}</th>
                    <th>{translate("Impact export")}</th>
                  </tr>
                </thead>
                <tbody>
                  {certificationList.length ? (
                    certificationList.map((certification) => (
                      <tr key={`${certification.id}-${certification.name}`}>
                        <td className="fw-semibold">{certification.name}</td>
                        <td className="small">{certification.supplier}</td>
                        <td className="small">{certification.certificateNumber || "-"}</td>
                        <td className="small">{certification.linkedProduct || "-"}</td>
                        <td className="small">{certification.expiryDate || "-"}</td>
                        <td>
                          <Badge bg={certification.status.toLowerCase().includes("valide") ? "success" : "warning"}>
                            {translate(certification.status)}
                          </Badge>
                        </td>
                        <td className="small">{certification.exportImpact || "-"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-muted small">{translate("no_certifications_available")}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg={4}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <h6 className="fw-semibold mb-3">{translate("Filieres fortes")}</h6>
              <div className="d-flex flex-wrap gap-2">
                {strongSectors.map((sector) => <Badge key={sector} bg="success">{translate(sector)}</Badge>)}
              </div>
              <h6 className="fw-semibold my-3">{translate("Filieres a developper")}</h6>
              <div className="d-flex flex-wrap gap-2">
                {weakSectors.map((sector) => <Badge key={sector} bg="warning" text="dark">{translate(sector)}</Badge>)}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <h6 className="fw-semibold mb-3">{translate("Activite recente")}</h6>
              <div className="d-flex flex-column gap-3">
                {activities.map((a, i) => (
                  <div key={`${a.name}-${i}`} className="d-flex align-items-start justify-content-between p-2 rounded-3" style={{ backgroundColor: "#f8fafc" }}>
                    <div>
                      <div className="fw-semibold small">{a.name}</div>
                      <div className="small text-muted">{a.date}</div>
                    </div>
                    <Badge bg={getActivityColor(a.type)}>{a.type}</Badge>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
            <Card.Body>
              <h6 className="fw-semibold mb-3">{translate("Alertes decisionnelles")}</h6>
              <div className="d-flex flex-column gap-3">
                {alerts.map((a, i) => {
                  const style = getAlertStyle(a.type);
                  return (
                    <div key={`${a.message}-${i}`} className="p-2 rounded-3" style={{ backgroundColor: style.bg }}>
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div style={{ color: style.color }}>
                          <div className="fw-semibold small">{translate(a.message)}</div>
                          <small className="text-muted">{a.date}</small>
                        </div>
                        <span className="badge" style={{ backgroundColor: style.color, color: "#fff" }}>{translate(a.type)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showSupplierProfile} onHide={() => setShowSupplierProfile(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{translate("Fiche fournisseur AIDA")} - {selectedSupplier.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col lg={7}>
              <Table responsive bordered className="align-middle">
                <tbody>
                  <tr><th>{translate("Nom de l'entreprise")}</th><td>{selectedSupplier.name}</td></tr>
                  <tr><th>{translate("Wilaya")}</th><td>{selectedSupplier.wilaya || "-"}</td></tr>
                  <tr><th>{translate("Adresse")}</th><td>{selectedSupplier.address || "-"}</td></tr>
                  <tr><th>{translate("Contact")}</th><td>{selectedSupplier.contact || "-"}</td></tr>
                  <tr><th>{translate("Filiere")}</th><td>{translate(selectedSupplier.sector)}</td></tr>
                  <tr><th>{translate("Produits fabriques")}</th><td>{selectedSupplier.products}</td></tr>
                  <tr><th>{translate("Capacite annuelle")}</th><td>{selectedSupplier.capacity}</td></tr>
                  <tr><th>{translate("Nombre d'employes")}</th><td>{selectedSupplier.employees}</td></tr>
                  <tr><th>{translate("Certifications ISO / IATF")}</th><td>{selectedSupplier.certifications}</td></tr>
                  <tr><th>{translate("Machines principales")}</th><td>{selectedSupplier.machines}</td></tr>
                  <tr><th>{translate("Documents fournisseurs")}</th><td>{selectedSupplier.documents}</td></tr>
                  <tr><th>{translate("Matiere premiere / potentiel")}</th><td>{translate(selectedSupplier.material)}</td></tr>
                  <tr><th>{translate("Niveau de maturite")}</th><td>{translate(selectedSupplier.maturity)}</td></tr>
                  <tr><th>{translate("Besoin d'accompagnement")}</th><td>{translate(selectedSupplier.supportNeed)}</td></tr>
                  <tr><th>{translate("Statut")}</th><td><Badge bg="info">{translate(selectedSupplier.status)}</Badge></td></tr>
                </tbody>
              </Table>
            </Col>
            <Col lg={5}>
              <Card className="border-0 bg-light mb-3" style={{ borderRadius: 8 }}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="fw-bold mb-0">{translate("Score AIDA fournisseur")}</h6>
                    <span className="fs-4 fw-bold">{selectedSupplier.score}/100</span>
                  </div>
                  <ProgressBar now={selectedSupplier.score} variant="success" />
                  <small className="text-muted d-block mt-2">{translate("Statut")}: {translate(selectedSupplier.status)}. {translate("Action recommandee")}: {translate(selectedSupplier.supportNeed)}.</small>
                </Card.Body>
              </Card>
              <div className="d-flex flex-column gap-2 mb-3">
                {maturityCriteria.map((criterion) => (
                  <div key={criterion.name}>
                    <div className="d-flex justify-content-between small mb-1">
                      <span>{translate(criterion.name)}</span>
                      <strong>{criterion.value}/100</strong>
                    </div>
                    <ProgressBar now={criterion.value} style={{ height: 6 }} />
                  </div>
                ))}
              </div>
              <h6 className="fw-bold">{translate("Photos produits / usine")}</h6>
              <Row className="g-2 mb-3">
                {["Produit", "Machine", "Ligne", "Usine"].map((item) => (
                  <Col xs={6} key={item}>
                    <div className="bg-light rounded-3 d-flex align-items-center justify-content-center" style={{ height: 86 }}>
                      <FaTools className="me-2 text-muted" /> <span className="small text-muted">{translate(item)}</span>
                    </div>
                  </Col>
                ))}
              </Row>
              <h6 className="fw-bold">{translate("Preuves industrielles")}</h6>
              <div className="d-flex flex-column gap-2">
                {supplierProofs.map((proof) => (
                  <div key={proof.name} className="d-flex justify-content-between align-items-center p-2 rounded-3" style={{ backgroundColor: "#f8fafc" }}>
                    <span className="small">{translate(proof.name)}: {proof.value}</span>
                    <Badge bg={proof.status === "Valide" ? "success" : proof.status === "Non conforme" ? "danger" : "warning"}>{translate(proof.status)}</Badge>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-dark" onClick={() => exportReport("Fiche fournisseur")}>
            <FaDownload className="me-2" /> {translate("Exporter fiche fournisseur PDF")}
          </Button>
          <Button variant="dark" onClick={() => setShowSupplierProfile(false)}>{translate("Fermer")}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

